import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Rating,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";

import { useAuth } from "../context/AuthContext";
import {
  authFetch,
  buildApiErrorFromResponse,
  getApiErrorMessage,
  isAuthRequiredError,
  isBackendUnavailableError,
} from "../utils/apiClient";
import { apiUrl } from "../utils/apiBaseUrl";

const REPORT_NOT_READY_MAX_ATTEMPTS = 5;
const REPORT_NOT_READY_RETRY_DELAY_MS = 1000;

function getReportErrorPresentation(errorKind) {
  switch (errorKind) {
    case "auth_required":
      return {
        title: "Sign in again to view this report.",
        description: "Your session is no longer valid for report access.",
        canRetry: false,
      };
    case "not_found":
      return {
        title: "This report could not be found.",
        description: "The report link may be stale or the interview may not have finished saving.",
        canRetry: false,
      };
    case "not_ready":
      return {
        title: "Your report is still being prepared.",
        description: "The report was not ready after ending the interview. Retry to check again.",
        canRetry: true,
      };
    case "backend_unavailable":
      return {
        title: "The report service is unavailable.",
        description: "The backend did not respond. Retry once the service is back.",
        canRetry: true,
      };
    default:
      return {
        title: "The report could not be loaded.",
        description: "A client or server error interrupted report loading.",
        canRetry: true,
      };
  }
}

function parseScores(value) {
  if (!value) return null;
  try {
    const scores = typeof value === "string" ? JSON.parse(value) : value;
    if (!scores || typeof scores !== "object") return null;
    return {
      communication: typeof scores.communication === "number" ? scores.communication : null,
      clarity: typeof scores.clarity === "number" ? scores.clarity : null,
      structure: typeof scores.structure === "number" ? scores.structure : null,
      relevance: typeof scores.relevance === "number" ? scores.relevance : null,
      technical_depth: typeof scores.technical_depth === "number" ? scores.technical_depth : null,
    };
  } catch {
    return null;
  }
}

export default function ReportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const { sessionId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorKind, setErrorKind] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [experienceRating, setExperienceRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showPostEndFeedbackDialog, setShowPostEndFeedbackDialog] = useState(false);
  const [postEndPromptHandled, setPostEndPromptHandled] = useState(false);
  const isPostEndNavigation = Boolean(location.state?.openFeedbackDialog);

  const fetchReport = useCallback(async () => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      setErrorKind("");
      const token = await getToken();
      const maxAttempts = isPostEndNavigation ? REPORT_NOT_READY_MAX_ATTEMPTS : 1;
      let data;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const response = await authFetch(apiUrl(`/api/interview/reports/${sessionId}`), token, {
          headers: { "Content-Type": "application/json" },
        });
        if (response.ok) {
          data = await response.json();
          break;
        }

        const reportError = await buildApiErrorFromResponse(response, {
          defaultMessage: `Failed to fetch report (${response.status}).`,
        });
        const canPoll = response.status === 404 && isPostEndNavigation && attempt < maxAttempts - 1;
        if (!canPoll) {
          if (response.status === 404 && isPostEndNavigation) {
            reportError.code = "REPORT_NOT_READY";
            reportError.userMessage = "Report generation is taking longer than expected.";
          }
          throw reportError;
        }
        await new Promise((resolve) => setTimeout(resolve, REPORT_NOT_READY_RETRY_DELAY_MS));
      }

      setReport(data);
    } catch (requestError) {
      console.error("Error fetching report:", requestError);
      const status = Number(requestError?.status || 0);
      setErrorKind(
        requestError?.code === "REPORT_NOT_READY"
          ? "not_ready"
          : isBackendUnavailableError(requestError)
            ? "backend_unavailable"
            : isAuthRequiredError(requestError) || status === 401 || status === 403
              ? "auth_required"
              : status === 404
                ? "not_found"
                : "generic",
      );
      setError(getApiErrorMessage(requestError, {
        backendLabel: "report service",
        defaultMessage: "Unable to load report.",
      }));
    } finally {
      setLoading(false);
    }
  }, [getToken, isPostEndNavigation, sessionId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const metrics = useMemo(
    () => (report?.metrics && typeof report.metrics === "object" ? report.metrics : {}),
    [report],
  );
  const scores = useMemo(() => parseScores(report?.scores), [report?.scores]);
  const turnEvaluations = Array.isArray(metrics.turn_evaluations) ? metrics.turn_evaluations : [];
  const captureStatus = typeof metrics.capture_status === "string" ? metrics.capture_status : "COMPLETE";
  const isIncompleteCapture = captureStatus === "INCOMPLETE_NO_CANDIDATE_RESPONSE";
  const isPartialCapture = captureStatus === "INCOMPLETE_PARTIAL_CAPTURE"
    || captureStatus === "INCOMPLETE_FALLBACK_ONLY_CAPTURE";
  const isTooBriefCapture = captureStatus === "INCOMPLETE_TOO_BRIEF" || Boolean(metrics.low_evidence_word_count);
  const feedbackDegradation = report?.ai_feedback?.feedback_degradation && typeof report.ai_feedback.feedback_degradation === "object"
    ? report.ai_feedback.feedback_degradation
    : metrics.feedback_degradation && typeof metrics.feedback_degradation === "object"
      ? metrics.feedback_degradation
      : null;
  const sessionFeedback = metrics.session_feedback && typeof metrics.session_feedback === "object"
    ? metrics.session_feedback
    : null;
  const confidenceScore = typeof metrics.confidence_score === "number" ? metrics.confidence_score : null;
  const overallScore = Number(report?.overall_score || 0);
  const scoreLabel = isTooBriefCapture
    ? "Not scored"
    : overallScore >= 80 ? "Excellent" : overallScore >= 70 ? "Good" : overallScore >= 50 ? "Developing" : "Needs focus";
  const scoreItems = [
    { label: "Communication", value: scores?.communication, color: "#21C36F" },
    { label: "Confidence", value: confidenceScore, color: "#2BB7E8" },
    { label: "Technical Accuracy", value: scores?.technical_depth, color: "#4F6BFF" },
    { label: "Problem Solving", value: scores?.relevance, color: "#786BFF" },
    { label: "Structure & Clarity", value: scores?.structure ?? scores?.clarity, color: "#FF9F43" },
  ];
  const strengths = Array.isArray(report?.ai_feedback?.strengths) ? report.ai_feedback.strengths : [];
  const improvements = Array.isArray(report?.ai_feedback?.areas_for_improvement)
    ? report.ai_feedback.areas_for_improvement
    : [];
  const actionPlan = Array.isArray(report?.ai_feedback?.action_plan) ? report.ai_feedback.action_plan : [];

  useEffect(() => {
    if (!sessionFeedback) return;
    if (typeof sessionFeedback.rating === "number") setExperienceRating(sessionFeedback.rating);
    if (typeof sessionFeedback.comment === "string") setFeedback(sessionFeedback.comment);
  }, [sessionFeedback]);

  useEffect(() => {
    if (loading || postEndPromptHandled || sessionFeedback || !location.state?.openFeedbackDialog) return;
    setShowPostEndFeedbackDialog(true);
    setPostEndPromptHandled(true);
  }, [loading, postEndPromptHandled, sessionFeedback, location.state]);

  const persistSessionFeedback = useCallback(async () => {
    if (!report?.id) return;
    if (!experienceRating || experienceRating < 1) {
      setFeedbackError("Please select a rating before submitting.");
      return;
    }

    setFeedbackError("");
    setSavingFeedback(true);
    try {
      const token = await getToken();
      const payload = {
        rating: experienceRating,
        comment: feedback.trim() || null,
        submitted_at: new Date().toISOString(),
      };
      const response = await authFetch(apiUrl(`/api/interview/reports/${report.id}/feedback`), token, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Failed to save feedback (${response.status})`);
      const data = await response.json();
      setReport((previous) => previous ? {
        ...previous,
        metrics: { ...(previous.metrics || {}), session_feedback: data.session_feedback || payload },
      } : previous);
      setShowSuccessPopup(true);
      setShowPostEndFeedbackDialog(false);
    } catch (requestError) {
      setFeedbackError(requestError.message || "Failed to save feedback");
    } finally {
      setSavingFeedback(false);
    }
  }, [experienceRating, feedback, getToken, report]);

  const handleDownload = async () => {
    if (!sessionId) return;
    setDownloadLoading(true);
    try {
      const token = await getToken();
      const response = await authFetch(apiUrl(`/api/interview/reports/${sessionId}/download`, { format: "pdf" }), token);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `interview-report-${sessionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error("Download failed:", downloadError);
    } finally {
      setDownloadLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: "grid", placeItems: "center", minHeight: "60vh" }}><CircularProgress /></Box>;
  }

  if (error) {
    const errorPresentation = getReportErrorPresentation(errorKind);
    return (
      <Box sx={{ p: 4, maxWidth: 900, mx: "auto" }}>
        <Alert severity="error">
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{errorPresentation.title}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{errorPresentation.description}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>{error}</Typography>
        </Alert>
        <Box sx={{ mt: 2, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {errorPresentation.canRetry && <Button variant="contained" onClick={fetchReport}>Retry</Button>}
          <Button variant="outlined" onClick={() => navigate("/interviews")}>Back to Interviews</Button>
          <Button variant="outlined" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ px: { xs: 2.5, sm: 4, lg: 6 }, py: { xs: 2.5, md: 4 }, maxWidth: 1180, mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 1.5, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>Reports</Typography>
          <Typography variant="body2" color="text.secondary">Interview feedback and next steps</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />} onClick={handleDownload} disabled={downloadLoading} sx={{ minHeight: 44, borderRadius: 1.5, px: 2, fontWeight: 600 }}>
          {downloadLoading ? "Preparing..." : "Download Report"}
        </Button>
      </Box>

      {(isIncompleteCapture || isPartialCapture) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {isIncompleteCapture
            ? "No candidate response was captured, so this report is incomplete."
            : "Some responses were not fully captured, so this report uses partial evidence."}
        </Alert>
      )}

      {isTooBriefCapture && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Response captured ({metrics.candidate_word_count || "few"} words), but it was too brief for reliable scoring. No score was assigned; add context, your action, and a concrete result or metric.
        </Alert>
      )}

      {feedbackDegradation && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {feedbackDegradation.message || "Provider feedback was degraded; deterministic feedback was used and the scored report was preserved."}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} aria-label="Report sections" sx={{ minHeight: 42, mb: 3, borderBottom: "1px solid", borderColor: "divider", "& .MuiTab-root": { minHeight: 42, px: 2, textTransform: "none", fontSize: "0.78rem", fontWeight: 600 } }}>
        <Tab label="Overview" />
        <Tab label="Question Analysis" />
      </Tabs>

      {activeTab === 0 && (
        <Box sx={{ display: "grid", gap: 2.5 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={5}>
              <Card sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, boxShadow: "none", height: "100%" }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 700, mb: 2.5 }}>Overall Score</Typography>
                  <Box sx={{ display: "grid", placeItems: "center", textAlign: "center" }}>
                    <Box sx={{ position: "relative", width: 112, height: 112, display: "grid", placeItems: "center" }}>
                      <CircularProgress variant="determinate" value={100} size={112} thickness={3.5} sx={{ position: "absolute", color: "action.hover" }} />
                      <CircularProgress variant="determinate" value={Math.max(0, Math.min(overallScore, 100))} size={112} thickness={3.5} sx={{ position: "absolute", color: overallScore >= 70 ? "success.main" : overallScore >= 50 ? "warning.main" : "error.main" }} />
                      <Box><Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{isTooBriefCapture ? "—" : report?.overall_score ?? "—"}</Typography><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{scoreLabel}</Typography></Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 280 }}>
                      {report?.ai_feedback?.overall_summary || "Your interview feedback is ready to review."}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={7}>
              <Card sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, boxShadow: "none", height: "100%" }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 700, mb: 2.5 }}>Score Breakdown</Typography>
                  <Box sx={{ display: "grid", gap: 2 }}>
                    {scoreItems.map(({ label, value, color }) => (
                      <Box key={label}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{value ?? "—"}{value != null ? "%" : ""}</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.max(0, Math.min(Number(value) || 0, 100))} aria-label={`${label} score`} sx={{ height: 5, borderRadius: 999, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 999 } }} />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Card sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, boxShadow: "none", height: "100%" }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Strengths</Typography>
                  {(strengths.length ? strengths : ["No strengths were generated for this session."]).map((item, index) => <Typography key={`${item}-${index}`} variant="body2" color="text.secondary" sx={{ mb: 1 }}>• {item}</Typography>)}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, boxShadow: "none", height: "100%" }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Areas to Improve</Typography>
                  {(improvements.length ? improvements : ["No improvement areas were generated for this session."]).map((item, index) => <Typography key={`${item}-${index}`} variant="body2" color="text.secondary" sx={{ mb: 1 }}>• {item}</Typography>)}
                  {actionPlan.length > 0 && <><Divider sx={{ my: 2 }} /><Typography component="h3" variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Next actions</Typography>{actionPlan.map((item, index) => { const action = typeof item === "string" ? item : item?.action; return action ? <Typography key={`${action}-${index}`} variant="body2" color="text.secondary" sx={{ mb: 1 }}>• {action}</Typography> : null; })}</>}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {activeTab === 1 && (
        <Card sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, boxShadow: "none" }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Question-by-question breakdown</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Review the coaching guidance for each answer.</Typography>
            {turnEvaluations.length ? (
              <Box sx={{ display: "grid", gap: 1.5 }}>
                {turnEvaluations.map((turn, index) => (
                  <Box key={index} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Question {index + 1}</Typography>
                      <Typography variant="caption" color="text.secondary">Clarity {turn.clarity ?? "—"} · Communication {turn.communication ?? "—"} · Depth {turn.depth ?? "—"} · Relevance {turn.relevance ?? "—"}</Typography>
                    </Box>
                    {turn.rationale && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{turn.rationale}</Typography>}
                  </Box>
                ))}
              </Box>
            ) : <Alert severity="info">No question analysis is available for this session.</Alert>}
          </CardContent>
        </Card>
      )}

      {isIncompleteCapture && (
        <Card sx={{ mt: 3, border: "1px solid", borderColor: "divider", borderRadius: 2, boxShadow: "none" }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Next Steps</Typography>
            <Typography variant="body2" color="text.secondary">Submit each answer before moving to the next question, then finish the interview when you are ready.</Typography>
          </CardContent>
        </Card>
      )}

      {sessionFeedback && <Alert severity="success" sx={{ mt: 3 }}>Feedback submitted: {sessionFeedback.rating}/5</Alert>}

      {!sessionFeedback && (
        <Card sx={{ mt: 3, border: "1px solid", borderColor: "divider", borderRadius: 2, boxShadow: "none" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Rate Your Experience</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}><Typography variant="body2" sx={{ mb: 1 }}>How was your interview experience?</Typography><Rating value={experienceRating} onChange={(_, value) => setExperienceRating(value || 0)} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth multiline rows={3} label="Additional Feedback" value={feedback} onChange={(event) => setFeedback(event.target.value)} /></Grid>
            </Grid>
            <Box sx={{ mt: 2 }}><Button variant="contained" onClick={persistSessionFeedback} disabled={experienceRating === 0 || savingFeedback}>{savingFeedback ? "Saving..." : "Submit Rating"}</Button></Box>
            {feedbackError && <Alert severity="warning" sx={{ mt: 2 }}>{feedbackError}</Alert>}
          </CardContent>
        </Card>
      )}

      <Dialog open={showPostEndFeedbackDialog} onClose={() => setShowPostEndFeedbackDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Interview Ended</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Your interview has ended. Share quick feedback before you continue.</Typography>
          <Box sx={{ mb: 2 }}><Typography variant="subtitle2" sx={{ mb: 1 }}>Session Rating</Typography><Rating value={experienceRating} onChange={(_, value) => setExperienceRating(value || 0)} /></Box>
          <TextField fullWidth multiline rows={3} label="Comments (Optional)" value={feedback} onChange={(event) => setFeedback(event.target.value)} placeholder="How was the interview flow?" />
          {feedbackError && <Alert severity="warning" sx={{ mt: 2 }}>{feedbackError}</Alert>}
        </DialogContent>
        <DialogActions><Button onClick={() => setShowPostEndFeedbackDialog(false)}>Skip</Button><Button variant="contained" onClick={persistSessionFeedback} disabled={savingFeedback}>{savingFeedback ? "Saving..." : "Submit"}</Button></DialogActions>
      </Dialog>

      <Dialog open={showSuccessPopup} onClose={() => setShowSuccessPopup(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Thank you</DialogTitle>
        <DialogContent><Typography>Your feedback has been recorded.</Typography></DialogContent>
        <DialogActions><Button variant="contained" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
